import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface Notificacion {
  id: string;
  tipo: 'venta' | 'puja_superada' | 'anuncio_aprobado' | 'reserva';
  titulo: string;
  mensaje: string;
  producto_id?: string;
  leida: boolean;
  created_at: string;
}

interface Props {
  userId: string;
}

export default function NotificacionesCampanita({ userId }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const noLeidas = notificaciones.filter((n) => !n.leida).length;

  useEffect(() => {
    fetchNotificaciones();

    // Realtime: escuchar nuevas órdenes donde el vendedor es el usuario actual
    const profileSub = supabase
      .from('profiles')
      .select('id')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data: profileData }) => {
        if (!profileData?.id) return null;

        const profileId = profileData.id;

        // Suscripción a nuevas órdenes (ventas)
        const orderChannel = supabase
          .channel(`notif-orders-${userId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'orders',
              filter: `vendedor_id=eq.${profileId}`,
            },
            async (payload) => {
              const order = payload.new as any;
              const { data: prod } = await supabase
                .from('products')
                .select('titulo')
                .eq('id', order.producto_id)
                .maybeSingle();

              const nuevaNotif: Notificacion = {
                id: `order-${order.id}`,
                tipo: order.tipo_orden === 'auto_reserva' ? 'reserva' : 'venta',
                titulo: order.tipo_orden === 'auto_reserva' ? '¡Reserva de Auto!' : '¡Artículo Vendido!',
                mensaje: prod?.titulo
                  ? `"${prod.titulo}" fue ${order.tipo_orden === 'auto_reserva' ? 'reservado' : 'vendido'} exitosamente.`
                  : 'Tienes una nueva venta.',
                producto_id: order.producto_id,
                leida: false,
                created_at: new Date().toISOString(),
              };
              setNotificaciones((prev) => [nuevaNotif, ...prev]);
            }
          )
          .subscribe();

        // Suscripción a pujas superadas (bids en mis productos)
        const bidChannel = supabase
          .channel(`notif-bids-${userId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'bids',
            },
            async (payload) => {
              const bid = payload.new as any;
              // Verificar si el producto es del vendedor actual
              const { data: prod } = await supabase
                .from('products')
                .select('titulo, vendedor_id')
                .eq('id', bid.producto_id)
                .maybeSingle();

              if (!prod) return;

              // Obtener profile_id del vendedor
              const { data: vendedorProfile } = await supabase
                .from('profiles')
                .select('id')
                .eq('user_id', userId)
                .maybeSingle();

              if (prod.vendedor_id !== vendedorProfile?.id) return;

              const nuevaNotif: Notificacion = {
                id: `bid-${bid.id}`,
                tipo: 'puja_superada',
                titulo: '¡Nueva Puja en tu Subasta!',
                mensaje: `Alguien pujó $${bid.monto?.toFixed(2)} en "${prod.titulo}".`,
                producto_id: bid.producto_id,
                leida: false,
                created_at: new Date().toISOString(),
              };
              setNotificaciones((prev) => [nuevaNotif, ...prev]);
            }
          )
          .subscribe();

        // Suscripción a aprobación de anuncios
        const productChannel = supabase
          .channel(`notif-products-${userId}`)
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'products',
              filter: `vendedor_id=eq.${profileId}`,
            },
            (payload) => {
              const prod = payload.new as any;
              const old = payload.old as any;
              if (prod.estado_aprobacion === 'aprobado' && old.estado_aprobacion !== 'aprobado') {
                const nuevaNotif: Notificacion = {
                  id: `prod-${prod.id}-aprobado`,
                  tipo: 'anuncio_aprobado',
                  titulo: '¡Anuncio Aprobado!',
                  mensaje: `Tu anuncio "${prod.titulo}" fue aprobado y ya está visible.`,
                  producto_id: prod.id,
                  leida: false,
                  created_at: new Date().toISOString(),
                };
                setNotificaciones((prev) => [nuevaNotif, ...prev]);
              }
            }
          )
          .subscribe();

        return () => {
          supabase.removeChannel(orderChannel);
          supabase.removeChannel(bidChannel);
          supabase.removeChannel(productChannel);
        };
      });

    // Cerrar dropdown al hacer clic fuera
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      profileSub.then((cleanup) => cleanup?.());
    };
  }, [userId]);

  const fetchNotificaciones = async () => {
    setLoading(true);
    try {
      // Obtener profile_id
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (!profileData?.id) { setLoading(false); return; }

      const profileId = profileData.id;

      // Últimas 20 órdenes como vendedor
      const { data: orders } = await supabase
        .from('orders')
        .select('id, producto_id, tipo_orden, created_at, estado')
        .eq('vendedor_id', profileId)
        .order('created_at', { ascending: false })
        .limit(10);

      // Últimas pujas en mis productos
      const { data: misProductos } = await supabase
        .from('products')
        .select('id, titulo')
        .eq('vendedor_id', profileId)
        .eq('tipo_venta', 'subasta')
        .limit(20);

      const notifs: Notificacion[] = [];

      // Convertir órdenes a notificaciones
      if (orders && orders.length > 0) {
        const productIds = orders.map((o) => o.producto_id).filter(Boolean);
        const { data: prods } = await supabase
          .from('products')
          .select('id, titulo')
          .in('id', productIds);

        const prodMap = new Map((prods || []).map((p) => [p.id, p.titulo]));

        for (const order of orders) {
          const titulo = prodMap.get(order.producto_id) || 'Artículo';
          notifs.push({
            id: `order-${order.id}`,
            tipo: order.tipo_orden === 'auto_reserva' ? 'reserva' : 'venta',
            titulo: order.tipo_orden === 'auto_reserva' ? '¡Reserva de Auto!' : '¡Artículo Vendido!',
            mensaje: `"${titulo}" fue ${order.tipo_orden === 'auto_reserva' ? 'reservado' : 'vendido'} exitosamente.`,
            producto_id: order.producto_id,
            leida: true, // históricas = ya leídas
            created_at: order.created_at,
          });
        }
      }

      // Últimas pujas en mis subastas
      if (misProductos && misProductos.length > 0) {
        const prodIds = misProductos.map((p) => p.id);
        const { data: bids } = await supabase
          .from('bids')
          .select('id, producto_id, monto, created_at')
          .in('producto_id', prodIds)
          .order('created_at', { ascending: false })
          .limit(5);

        const prodTitleMap = new Map(misProductos.map((p) => [p.id, p.titulo]));

        for (const bid of bids || []) {
          notifs.push({
            id: `bid-${bid.id}`,
            tipo: 'puja_superada',
            titulo: '¡Nueva Puja en tu Subasta!',
            mensaje: `Alguien pujó $${bid.monto?.toFixed(2)} en "${prodTitleMap.get(bid.producto_id) || 'tu subasta'}".`,
            producto_id: bid.producto_id,
            leida: true,
            created_at: bid.created_at,
          });
        }
      }

      // Ordenar por fecha
      notifs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setNotificaciones(notifs.slice(0, 15));
    } catch (err) {
      console.error('Error cargando notificaciones:', err);
    } finally {
      setLoading(false);
    }
  };

  const marcarTodasLeidas = () => {
    setNotificaciones((prev) => prev.map((n) => ({ ...n, leida: true })));
  };

  const handleNotifClick = (notif: Notificacion) => {
    setNotificaciones((prev) =>
      prev.map((n) => (n.id === notif.id ? { ...n, leida: true } : n))
    );
    if (notif.producto_id) {
      navigate(`/producto/${notif.producto_id}`);
    }
    setOpen(false);
  };

  const getIcono = (tipo: Notificacion['tipo']) => {
    switch (tipo) {
      case 'venta': return { icon: 'ri-shopping-bag-3-line', color: 'text-green-600', bg: 'bg-green-100' };
      case 'reserva': return { icon: 'ri-car-line', color: 'text-amber-600', bg: 'bg-amber-100' };
      case 'puja_superada': return { icon: 'ri-auction-line', color: 'text-orange-600', bg: 'bg-orange-100' };
      case 'anuncio_aprobado': return { icon: 'ri-checkbox-circle-line', color: 'text-emerald-600', bg: 'bg-emerald-100' };
      default: return { icon: 'ri-notification-3-line', color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  };

  const formatTiempo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'Ahora mismo';
    if (mins < 60) return `Hace ${mins}m`;
    if (hrs < 24) return `Hace ${hrs}h`;
    return `Hace ${days}d`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Botón campanita */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
        title="Notificaciones"
      >
        <i className="ri-notification-3-line text-xl text-gray-700"></i>
        {noLeidas > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
            {noLeidas > 9 ? '9+' : noLeidas}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl border border-gray-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Notificaciones</h3>
              {noLeidas > 0 && (
                <p className="text-xs text-gray-500">{noLeidas} sin leer</p>
              )}
            </div>
            {noLeidas > 0 && (
              <button
                onClick={marcarTodasLeidas}
                className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
              >
                Marcar todas leídas
              </button>
            )}
          </div>

          {/* Lista */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin"></div>
              </div>
            ) : notificaciones.length === 0 ? (
              <div className="text-center py-10 px-4">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-notification-off-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-sm text-gray-500">Sin notificaciones aún</p>
                <p className="text-xs text-gray-400 mt-1">Aquí verás tus ventas y pujas</p>
              </div>
            ) : (
              notificaciones.map((notif) => {
                const { icon, color, bg } = getIcono(notif.tipo);
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleNotifClick(notif)}
                    className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer text-left border-b border-gray-50 last:border-0 ${
                      !notif.leida ? 'bg-amber-50/40' : ''
                    }`}
                  >
                    <div className={`w-9 h-9 ${bg} rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <i className={`${icon} text-base ${color}`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-xs font-bold truncate ${!notif.leida ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notif.titulo}
                        </p>
                        {!notif.leida && (
                          <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notif.mensaje}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{formatTiempo(notif.created_at)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notificaciones.length > 0 && (
            <div className="border-t border-gray-100 px-4 py-2.5">
              <button
                onClick={() => { fetchNotificaciones(); }}
                className="w-full text-xs text-gray-500 hover:text-gray-700 cursor-pointer text-center"
              >
                <i className="ri-refresh-line mr-1"></i>
                Actualizar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
